/**
 * Le combat (GDD §4). Module pur.
 *
 * Le point de calibrage : dans les données officielles, les PV d'un monstre
 * valent environ 11 × son niveau. Toutes les constantes ci-dessous sont réglées
 * pour qu'un personnage de niveau N farme une famille de niveau N de façon
 * stable, et se mette en difficulté nettement au-dessus.
 */
import type { Personnage } from "./etat.ts"
import type { Contenu, Monstre, Palier } from "./contenu.ts"
import { bonusEquipement } from "./objets.ts"
import { effetDe } from "./classes.ts"
import type { EffetDofus } from "./dofus.ts"
import { famille, monstre } from "./contenu.ts"

export const ELEMENTS = ["terre", "feu", "eau", "air"] as const
export type Element = (typeof ELEMENTS)[number]

/**
 * Les dégâts d'un monstre ne figurent pas dans le dump (ils dépendraient de la
 * simulation complète de ses sorts). On les dérive de son niveau. C'est une
 * approximation de game designer, la seule valeur non officielle du modèle.
 *
 * **Recalibrée avec la fin de la régénération.** L'ancienne valeur — `1,5 +
 * 0,55 × niveau` — était réglée pour être compensée par une guérison continue
 * qui n'existe plus. Mesurée sans elle, elle vidait la barre de vie en trente
 * secondes et quatre à huit victoires, à tous les niveaux : le farm s'arrêtait
 * avant d'avoir commencé. Divisée par cinq, une barre pleine vaut une quarantaine
 * de victoires, et un repas en rend une dizaine.
 */
export const dpsMonstre = (niveau: number) => 0.3 + niveau * 0.11

// ---------------------------------------------------------------- personnage

export interface StatsPerso {
  pvMax: number
  /** multiplicateur appliqué aux soins consommés */
  soin: number
  /** dégâts bruts par élément, avant résistance du monstre */
  degats: Record<Element, number>
  /** bonus de taux de drop, en % (GDD §4) */
  prospection: number
  /** réduction moyenne des dégâts subis, en % */
  resistance: number
  /** bonus d'XP, en % */
  sagesse: number
  /** multiplicateur de cadence de combat */
  /** part des coups qui frappent en critique, en pourcentage */
  critique: number
  /** dégâts ajoutés par un coup critique */
  domCritique: number
  /** part des coups reçus qui ne portent pas */
  esquive: number
  /** secondes d'ouverture au début de chaque combat */
  entameS: number
  /** apport des invocations aux dégâts */
  invocation: number
  /** capacité de transport, qui agrandit le coffre */
  pods: number
  /** niveau du personnage : l'écart avec celui du monstre décide de la casse */
  niveau: number
  /** points d'action, six de base plus ceux de l'équipement */
  pa: number
  /** points de mouvement, trois de base plus ceux de l'équipement */
  pm: number
  vitesse: number
  /** multiplicateur de cadence de récolte */
  recolte: number
}

/** La caractéristique qui alimente chaque élément. */
const CARAC_DE: Record<Element, "force" | "intelligence" | "chance" | "agilite"> = {
  terre: "force", feu: "intelligence", eau: "chance", air: "agilite",
}

/**
 * Décomposition des dégâts d'un élément.
 *
 * Existe pour que l'écran puisse **montrer** le calcul au lieu de lâcher un
 * nombre : « 265 » sans explication, à côté d'une Force de 224, n'apprend rien.
 * `statsPerso` s'en sert aussi, pour que l'affiché et le calculé ne divergent
 * jamais.
 */
/**
 * Les points d'action, traduits en cadence.
 *
 * Il n'y a pas de tour de jeu ici : le combat est continu, un personnage frappe
 * en permanence. Les PA n'ont donc rien à piloter — et les ~190 objets qui en
 * donnent, plus les 411 paliers de panoplie qui en accordent, étaient de la
 * statistique morte. On leur rend leur sens d'origine : chez Ankama on agit
 * avec 6 PA, chacun de plus est un septième d'action gagné. Ici, chacun de plus
 * accélère les frappes d'un sixième.
 *
 * Les PM font pareil pour la récolte : on se déplace de 3 cases, chaque point
 * en plus rapproche la ressource suivante d'un tiers.
 */
export const PA_BASE = 6
export const PM_BASE = 3
export const cadenceDePa = (pa: number) => (PA_BASE + pa) / PA_BASE
export const cadenceDePm = (pm: number) => (PM_BASE + pm) / PM_BASE

/**
 * Les quatre systèmes qui donnent enfin un effet aux statistiques mortes.
 *
 * Elles couvrent la moitié du catalogue et n'en avaient aucun : la fiche d'un
 * objet était à moitié décorative. Chacune garde le sens qu'elle a chez Ankama,
 * traduite dans un combat continu.
 *
 * **Critique.** Une part des coups frappe plus fort : `% Critique` est cette
 * part, `Dommage Critiques` ce qu'elle ajoute. Un critique vaut aussi la moitié
 * de dégâts en plus par lui-même, sans quoi le % Critique seul ne servirait à
 * rien — et 846 objets n'en donnent que lui.
 *
 * **Esquive** (Fuite + Tacle). Se dérober ou clouer l'adversaire revient au
 * même ici : une part des coups reçus ne porte pas. Les valeurs montent haut —
 * jusqu'à 355 cumulés — d'où une courbe qui sature et un plafond ferme.
 *
 * **Entame** (Initiative + Portée). Agir le premier, et de loin, c'est un
 * moment de chaque combat où l'adversaire ne rend pas les coups. Cela se compte
 * en secondes retirées à chaque mise à mort, et non en pourcentage continu :
 * c'est ce qui la distingue de l'esquive, et ce qui la rend précieuse sur les
 * combats courts.
 *
 * **Invocation.** Une créature se bat à vos côtés : elle ajoute sa part de
 * dégâts.
 */
export const CRIT_BONUS = 0.5
export const ESQUIVE_MAX = 0.35
export const ESQUIVE_DEMI = 400
export const ENTAME_MAX_S = 1
export const INVOC_PAR_POINT = 0.15
export const INVOC_MAX = 0.6

/** Part des coups reçus qui ne portent pas. Sature : le cumul monte très haut. */
export const esquiveDe = (fuite: number, tacle: number): number => {
  const x = Math.max(0, fuite + tacle)
  return Math.min(ESQUIVE_MAX, x / (x + ESQUIVE_DEMI))
}

/** Secondes d'ouverture, au début de chaque combat, où l'on ne prend rien. */
export const entameDe = (portee: number, initiative: number): number =>
  Math.min(ENTAME_MAX_S, Math.max(0, portee) * 0.05 + Math.max(0, initiative) / 6000)

/** Ce que les invocations ajoutent aux dégâts. */
export const invocationDe = (n: number): number =>
  Math.min(INVOC_MAX, Math.max(0, n) * INVOC_PAR_POINT)

export interface DetailDegats {
  /** dégâts de base, indépendants de tout */
  socle: number
  carac: number
  equip: number
  puissance: number
  /** points de « Dommages » de l'équipement, qui pèsent triple */
  dommages: number
  /** dégâts critiques moyens : la part de coups critiques × leur bonus */
  critique: number
  /** multiplicateur de classe × Dofus */
  facteur: number
  /** apport des invocations, en multiplicateur */
  invocation: number
  total: number
}

/**
 * Dégâts de base, indépendants de toute caractéristique.
 *
 * Il valait 2 quand le moteur additionnait les quatre éléments : un personnage
 * neuf frappait donc en réalité à 8. En passant au meilleur élément seul, le
 * socle ne comptait plus qu'une fois et les premières minutes du jeu
 * devenaient quatre fois plus lentes. On le porte à ce qu'il valait
 * effectivement. Passé quelques niveaux il ne pèse plus rien : à 100 en Force
 * on est à plus de 90 de dégâts, le socle en fait moins d'un dixième.
 */
const SOCLE = 8
const PAR_POINT = 0.9
const PAR_DOMMAGE = 3

export function detailDegats(
  p: Personnage, el: Element, contenu?: Contenu, compte?: EffetDofus,
): DetailDegats {
  const b = bonusEquipement(p.equipement, contenu)
  const k = CARAC_DE[el]
  const facteur = (effetDe(p.classe).degats ?? 1) * (compte?.degats ?? 1)
  // Le taux de critique est une probabilité : au-delà de 100 % il ne veut plus
  // rien dire, et les cumuls d'équipement le dépassent.
  const taux = Math.min(1, Math.max(0, b.critique) / 100)
  const d = {
    socle: SOCLE, carac: p.carac[k], equip: b[k], puissance: b.puissance,
    dommages: b.dommages[el], critique: taux * b.domCritique,
    facteur, invocation: 1 + invocationDe(b.invocation), total: 0,
  }
  const brut = SOCLE + (d.carac + d.equip + d.puissance) * PAR_POINT
    + (d.dommages + d.critique) * PAR_DOMMAGE
  // Un coup critique frappe plus fort par lui-même : sans cela, les 846 objets
  // qui ne donnent que du % Critique n'apporteraient toujours rien.
  d.total = brut * (1 + taux * CRIT_BONUS) * d.invocation * facteur
  return d
}

/**
 * @param compte effets valables pour tout le compte, soit les Dofus obtenus (GDD §11).
 */
export function statsPerso(p: Personnage, contenu?: Contenu, compte?: EffetDofus): StatsPerso {
  const c = p.carac
  const b = bonusEquipement(p.equipement, contenu)
  const e = effetDe(p.classe)
  const vit = c.vitalite + b.vitalite
  const r = b.resistances
  return {
    pvMax: 50 + vit * 5 + p.niveau * 10,
    /**
     * **Ce n'est plus une régénération**, c'est un multiplicateur de soin.
     *
     * Les points de vie perdus le restent : on ne récupère qu'en mangeant. Le
     * Soin de l'équipement, le bonus de l'Eniripsa et celui du Dofus Cawotte
     * gardent tout leur sens, mais ils portent désormais sur ce que rend un
     * pain, pas sur une guérison qui tombait du ciel.
     */
    soin: (1 + (b.soin + vit * 0.05) / 100) * (e.regen ?? 1) * (compte?.regen ?? 1),
    degats: {
      terre: detailDegats(p, "terre", contenu, compte).total,
      feu: detailDegats(p, "feu", contenu, compte).total,
      eau: detailDegats(p, "eau", contenu, compte).total,
      air: detailDegats(p, "air", contenu, compte).total,
    },
    prospection: b.prospection + (e.prospection ?? 0) + (compte?.prospection ?? 0),
    resistance: (r.terre + r.feu + r.eau + r.air) / 4 + (e.resistance ?? 0),
    sagesse: c.sagesse + b.sagesse,
    niveau: p.niveau,
    critique: Math.min(100, b.critique),
    domCritique: b.domCritique,
    esquive: esquiveDe(b.fuite, b.tacle),
    entameS: entameDe(b.portee, b.initiative),
    invocation: invocationDe(b.invocation),
    pods: b.pods,
    pa: PA_BASE + b.pa,
    pm: PM_BASE + b.pm,
    /** multiplicateur de cadence : accélère combats et récoltes (Xélor) */
    vitesse: cadenceDePa(b.pa) * (e.vitesse ?? 1) * (compte?.vitesse ?? 1),
    recolte: cadenceDePm(b.pm) * (e.recolte ?? 1) * (compte?.recolte ?? 1),
  }
}

// ---------------------------------------------------------------- combat

/** Palier retenu pour un monstre. M1 n'expose que le premier ; la sélection de
 *  palier viendra avec les zones. */
export const palierDe = (m: Monstre): Palier => m.paliers[0]!

/**
 * Plafond de résistance, pour un monstre comme pour un personnage.
 *
 * **Sans lui, dix monstres étaient invincibles.** Les résistances viennent des
 * données de Dofus, où elles dépassent parfois cent : Gourlo le Terrible en a
 * deux cents dans les cinq éléments, le Kralamoure Géant neuf cents. Or la
 * formule les soustrait — `dégâts × (1 − res/100)` — et au-delà de cent le
 * facteur devient négatif : le plancher de 0,1 dégât par seconde prenait le
 * relais, et il fallait six heures pour entamer un boss qui frappe en retour.
 * Balbouz l'a signalé sur la Cale de l'Arche d'Otomaï : « Gourlo est quasi
 * imbattable à moins de dépenser xxxx vivres ». Il n'était pas dur, il était
 * impossible.
 *
 * Quatre-vingts, comme pour le joueur (voir `degatsSubisPs`) : un adversaire
 * peut réduire les coups à un cinquième, jamais les annuler. Un boss reste cinq
 * fois plus long à abattre qu'une cible nue — ce qu'on attend d'un boss.
 */
export const PLAFOND_RES = 80

/** La résistance effective d'un palier sur un élément. */
export const resDe = (pal: Palier, e: keyof Palier["res"]): number =>
  Math.min(PLAFOND_RES, pal.res[e])

/**
 * Dégâts par seconde contre un monstre donné.
 *
 * Chaque élément est atténué par la résistance correspondante. Une résistance
 * négative est une faiblesse et amplifie les dégâts, ce qui rend le
 * choix d'élément décisif, et le fait qu'une famille soit hétérogène
 * empêche qu'un élément unique domine (GDD §4).
 */
export function dpsContre(s: StatsPerso, pal: Palier): number {
  // **Le meilleur élément, pas leur somme.** Le GDD §4 pose
  // `DPS = dégâts_élément × (1 − résistance)`, au singulier, et la fiche du
  // personnage annonce « tu frappes avec ton meilleur élément » — mais le code
  // additionnait les quatre. On lisait donc 8,8 dégâts/s sur sa fiche et on en
  // infligeait 16,9. Pire, cela récompensait l'éparpillement : quatre éléments
  // à quart de valeur pesaient autant qu'un seul à pleine valeur, et le socle
  // comptait quatre fois.
  //
  // Le choix reste par monstre : un build hybride a une réponse correcte à
  // chacun, un build mono une excellente réponse à certains et une mauvaise aux
  // autres. C'est exactement l'arbitrage décrit au §4.
  let meilleur = 0
  for (const e of ELEMENTS) meilleur = Math.max(meilleur, s.degats[e] * (1 - resDe(pal, e) / 100))
  return Math.max(0.1, meilleur)
}

/** L'élément qui porte le mieux contre ce palier : celui qu'on joue. */
export function elementContre(s: StatsPerso, pal: Palier): Element {
  return ELEMENTS.reduce((a, e) =>
    s.degats[e] * (1 - resDe(pal, e) / 100) > s.degats[a] * (1 - resDe(pal, a) / 100) ? e : a, ELEMENTS[0])
}

/**
 * Durée plancher d'un combat.
 *
 * Sans elle, un personnage un peu monté « tue » les monstres de bas niveau en
 * une fraction de seconde, ce qui rend le bas niveau plus rentable en XP/s que
 * le haut niveau : la progression s'inverse et le joueur n'a plus jamais de
 * raison d'avancer. Le plancher rétablit l'ordre : à débit égal, un monstre
 * plus haut rapporte plus.
 */
export const TEMPS_KILL_MIN_S = 1

// Le plancher s'applique **après** la cadence, sinon une classe rapide passe
// dessous : un Xélor tuait en 0,77 s, l'animation n'avait plus qu'un seul coup
// à jouer et affichait toute la vie du monstre en un chiffre.
export const tempsParKill = (s: StatsPerso, pal: Palier) =>
  Math.max(TEMPS_KILL_MIN_S, pal.pv / (dpsContre(s, pal) * s.vitesse))

/**
 * Temps qu'on tient face à ce palier, en secondes.
 *
 * Il n'y a plus de régénération : les points de vie perdus le restent, et on ne
 * récupère qu'en **mangeant**. Un farm n'est donc jamais « soutenable » en soi ;
 * il dure aussi longtemps que la réserve de vie et les vivres le permettent.
 * La Vitalité décide combien de temps on tient, les vivres combien de fois on
 * repart.
 */
export const tenue = (s: StatsPerso, pal: Palier, pv: number): number =>
  pv / Math.max(0.01, degatsSubisPs(s, pal))

/**
 * Ce que coûte de s'attaquer à bien plus haut que soi.
 *
 * Les dégâts d'un monstre croissaient **linéairement** avec son niveau, tout
 * comme les points de vie du joueur : le rapport restait donc à peu près
 * constant, et quadrupler le niveau de la cible ne divisait la survie que par
 * trois. Or l'expérience, elle, est multipliée par sept sur le même écart et
 * n'est jamais atténuée vers le haut. Viser quatre fois son niveau était donc
 * la stratégie optimale — un niveau 32 tenait deux minutes contre du niveau 120.
 *
 * L'écart pèse désormais de lui-même : un monstre deux fois plus haut frappe
 * près de trois fois plus fort. À son propre niveau, rien ne change ; un peu
 * au-dessus reste un pari tenable ; très au-dessus, on tombe en quelques
 * secondes, ce qui est le propos.
 */
export const EXPOSANT_ECART = 1.5

/**
 * Plancher du niveau de référence.
 *
 * L'écart se mesure en **rapport**, ce qui n'a aucun sens tout en bas : au
 * niveau 1, un monstre de niveau 6 donne un rapport de six et frapperait
 * quinze fois plus fort — les premières minutes du jeu devenaient
 * infranchissables. En dessous de ce plancher, l'écart ne pèse pas : de toute
 * façon rien n'y est dangereux.
 */
export const NIVEAU_PLANCHER = 10

export const facteurEcart = (niveauMonstre: number, niveauJoueur: number): number =>
  Math.pow(Math.max(1, niveauMonstre / Math.max(NIVEAU_PLANCHER, niveauJoueur)), EXPOSANT_ECART)

/**
 * Dégâts encaissés par seconde, une fois tout ce qui protège appliqué.
 *
 * Trois choses réduisent la casse, et elles se composent : les **résistances**
 * atténuent chaque coup, l'**esquive** en fait rater une partie, et l'**entame**
 * retire à chaque combat les secondes où l'adversaire ne riposte pas encore.
 * L'entame se rapporte à la durée du combat : sur un adversaire qui tombe en
 * une seconde elle vaut tout, sur un boss elle ne pèse presque rien — c'est ce
 * qui la distingue de l'esquive et lui donne son propre usage.
 */
export const degatsSubisPs = (s: StatsPerso, pal: Palier) => {
  const brut = dpsMonstre(pal.niveau) * facteurEcart(pal.niveau, s.niveau)
    * (1 - Math.min(80, s.resistance) / 100)
  const duree = tempsParKill(s, pal)
  const part = duree > 0 ? Math.max(0, 1 - s.entameS / duree) : 1
  return brut * (1 - s.esquive) * part
}

export interface Verdict {
  stable: boolean
  /** secondes avant la mort si le farm n'est pas soutenable */
  survieS: number | null
  tempsParKillS: number
  degatsSubisPs: number
  /** dégâts infligés par seconde, soit la puissance réelle contre cette cible */
  dpsInfliges: number
  /**
   * Expérience d'**une victoire**, atténuation de niveau comprise.
   *
   * C'est ce qu'on gagne : abattre un monstre rapporte ce que vaut ce monstre,
   * qu'il ait pris une seconde ou quatre cents. Le débit ci-dessous n'est qu'un
   * outil de comparaison entre familles, et l'afficher seul faisait croire que
   * l'expérience se comptait au temps passé.
   */
  xpParVictoire: number
  /**
   * Expérience par seconde : le repère qui permet de **choisir** entre deux
   * familles. Sans lui, une famille très basse paraît excellente parce qu'elle
   * se tue vite ; avec lui seul, on ne comprend plus ce qu'on gagne.
   */
  xpParSeconde: number
}

export function verdictContre(s: StatsPerso, pal: Palier, pv: number, niveauJoueur = 0): Verdict {
  return {
    xpParVictoire: xpGagnee(pal.niveau, niveauJoueur),
    xpParSeconde: xpGagnee(pal.niveau, niveauJoueur) / tempsParKill(s, pal),
    // Plus rien n'est « stable » : sans régénération, tout farm finit par
    // vider la barre de vie. Ce qui varie, c'est le temps qu'on tient.
    stable: false,
    survieS: tenue(s, pal, pv),
    tempsParKillS: tempsParKill(s, pal),
    degatsSubisPs: degatsSubisPs(s, pal),
    // La cadence en fait partie : à dégâts égaux, frapper plus souvent rapporte
    // plus, et c'est ce que le joueur lit.
    dpsInfliges: dpsContre(s, pal) * s.vitesse,
  }
}

/**
 * Verdict pour une famille entière (GDD §4).
 *
 * Le temps par kill affiché est la moyenne sur la rotation, mais la survie se
 * juge sur le monstre LE PLUS DANGEREUX : c'est lui qui arrêtera le farm, pas
 * la moyenne.
 */
export function verdictFamille(
  s: StatsPerso, c: Contenu, familleId: number, pv: number, niveauJoueur = 0,
): Verdict | null {
  const f = famille(c, familleId)
  if (!f) return null
  const paliers = f.monstres.map((id) => monstre(c, id)).filter((m): m is Monstre => Boolean(m)).map(palierDe)
  if (paliers.length === 0) return null

  const moyenneKill = paliers.reduce((a, p) => a + tempsParKill(s, p), 0) / paliers.length
  const moyenneDps = paliers.reduce((a, p) => a + dpsContre(s, p) * s.vitesse, 0) / paliers.length
  const pire = paliers.reduce((a, b) => (a.niveau >= b.niveau ? a : b))
  const v = verdictContre(s, pire, pv, niveauJoueur)
  // L'XP est celle de la famille entière, pas celle du monstre le plus rude :
  // le farm tourne sur toute la rotation.
  const moyenneXp = paliers.reduce(
    (a, p) => a + xpGagnee(p.niveau, niveauJoueur) / tempsParKill(s, p), 0) / paliers.length
  const moyenneParVictoire = paliers.reduce(
    (a, p) => a + xpGagnee(p.niveau, niveauJoueur), 0) / paliers.length
  return {
    ...v, tempsParKillS: moyenneKill, dpsInfliges: moyenneDps,
    xpParVictoire: moyenneParVictoire, xpParSeconde: moyenneXp,
  }
}

/**
 * Résistances moyennes d'une famille, élément par élément.
 *
 * C'est l'information qui décide de la caractéristique à monter, et elle
 * n'était affichée nulle part : on choisissait son élément à l'aveugle. Une
 * moyenne plutôt que le détail par monstre, parce qu'on farme la rotation
 * entière, pas un monstre en particulier.
 *
 * Négatif = faiblesse : on y frappe plus fort.
 */
export function resistancesFamille(
  c: Contenu, familleId: number,
): Record<"terre" | "feu" | "eau" | "air", number> | null {
  const f = famille(c, familleId)
  if (!f) return null
  const paliers = f.monstres.map((id) => monstre(c, id)).filter((m): m is Monstre => Boolean(m)).map(palierDe)
  if (paliers.length === 0) return null
  const moy = { terre: 0, feu: 0, eau: 0, air: 0 }
  // La bande annonce la résistance **effective** : afficher 200 % quand le
  // combat en applique 80 ferait mentir la fiche.
  for (const e of ELEMENTS) moy[e] = Math.round(paliers.reduce((a, p) => a + resDe(p, e), 0) / paliers.length)
  return moy
}

// ---------------------------------------------------------------- progression

/**
 * XP requise pour passer du niveau n au suivant.
 *
 * Exposant 2,4 : les premiers niveaux tombent en minutes, ceux de la
 * cinquantaine demandent une demi-heure. Une courbe plus plate faisait grimper
 * un personnage au-delà du contenu disponible en une seule soirée.
 */
export const xpRequise = (n: number) => Math.round(100 * Math.pow(n, 2.4))

/**
 * XP accordée par un monstre. Superlinéaire, pour que monter de palier soit
 * franchement rentable et pas seulement équivalent.
 */
/**
 * Expérience d'une victoire, **atténuée quand la cible est sous le joueur**.
 *
 * Sans cette atténuation la progression s'inversait : le plancher d'une seconde
 * par kill fait qu'un joueur de niveau 150 abat un monstre de niveau 40 aussi
 * vite qu'un de niveau 150, et l'XP brute d'un monstre bas restait assez haute
 * pour dominer. Mesuré : à 150, la meilleure XP/seconde venait de monstres de
 * niveau 62, et tout le contenu au-dessus ne valait jamais le détour.
 *
 * Le facteur `niveauMonstre / niveauJoueur` suffit à remettre l'ordre : la
 * meilleure cible suit alors le niveau du joueur (à 100 → 102, à 150 → 162, à
 * 200 → 200). C'est aussi la règle du jeu d'origine, où farmer sous son niveau
 * rapporte de moins en moins.
 */
export const xpGagnee = (niveauMonstre: number, niveauJoueur = 0) => {
  const brut = 5 * Math.pow(niveauMonstre, 1.4)
  const facteur = niveauJoueur > niveauMonstre ? niveauMonstre / niveauJoueur : 1
  return Math.round(brut * facteur)
}

/** Points de caractéristique accordés par niveau (GDD §3). */
export const POINTS_PAR_NIVEAU = 5
